/**
 * Safe Storage Utility
 *
 * Provides safe wrappers around sessionStorage and localStorage that handle:
 * - Private browsing mode (where storage may be disabled)
 * - Storage quota exceeded errors
 * - Other storage access errors
 *
 * Includes a memory fallback for when storage is unavailable.
 */

// Memory fallback storage for when browser storage is unavailable
const memoryStorage = new Map<string, string>();

/**
 * Safely get an item from sessionStorage
 * Returns null on any error (same as if key doesn't exist)
 */
export function safeGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (_e) {
    // Storage access failed (private browsing, disabled, etc.)
    // Fall back to memory storage
    return memoryStorage.get(key) ?? null;
  }
}

/**
 * Safely set an item in sessionStorage
 * Returns true if successful, false if failed
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (_e) {
    // Storage access failed - try memory fallback
    try {
      memoryStorage.set(key, value);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Safely remove an item from sessionStorage
 * Returns true if successful, false if failed
 */
export function safeRemoveItem(key: string): boolean {
  try {
    sessionStorage.removeItem(key);
    memoryStorage.delete(key); // Also remove from memory fallback
    return true;
  } catch (_e) {
    // At least try to remove from memory fallback
    memoryStorage.delete(key);
    return false;
  }
}

/**
 * Safely clear all items from sessionStorage
 * Returns true if successful, false if failed
 */
export function safeClear(): boolean {
  try {
    sessionStorage.clear();
    memoryStorage.clear();
    return true;
  } catch (_e) {
    memoryStorage.clear();
    return false;
  }
}

/**
 * Get an item and parse it as JSON
 * Returns null if the item doesn't exist or can't be parsed
 */
export function safeGetJSON<T>(key: string): T | null {
  const value = safeGetItem(key);
  if (value === null) return null;

  try {
    return JSON.parse(value) as T;
  } catch (_e) {
    return null;
  }
}

/**
 * Stringify and set a value as JSON
 * Returns true if successful, false if failed
 */
export function safeSetJSON<T>(key: string, value: T): boolean {
  try {
    const stringValue = JSON.stringify(value);
    return safeSetItem(key, stringValue);
  } catch (_e) {
    return false;
  }
}

/**
 * Object-style API for sessionStorage that mirrors the native API
 * but with safe error handling
 */
export const safeSessionStorage = {
  getItem: safeGetItem,
  setItem: (key: string, value: string): void => {
    safeSetItem(key, value);
  },
  removeItem: (key: string): void => {
    safeRemoveItem(key);
  },
  clear: (): void => {
    safeClear();
  },
  // Extended methods
  getJSON: safeGetJSON,
  setJSON: safeSetJSON,
};

/**
 * Resilient storage that always succeeds by using memory fallback
 * Use this when you absolutely need storage to work
 */
export const resilientStorage = {
  getItem(key: string): string | null {
    const result = safeGetItem(key);
    if (result !== null) return result;
    return memoryStorage.get(key) ?? null;
  },

  setItem(key: string, value: string): void {
    if (!safeSetItem(key, value)) {
      memoryStorage.set(key, value);
    }
  },

  removeItem(key: string): void {
    safeRemoveItem(key);
    memoryStorage.delete(key);
  },

  clear(): void {
    safeClear();
    memoryStorage.clear();
  },

  getJSON<T>(key: string): T | null {
    const result = safeGetJSON<T>(key);
    if (result !== null) return result;

    const memValue = memoryStorage.get(key);
    if (memValue) {
      try {
        return JSON.parse(memValue) as T;
      } catch {
        return null;
      }
    }
    return null;
  },

  setJSON<T>(key: string, value: T): void {
    if (!safeSetJSON(key, value)) {
      try {
        memoryStorage.set(key, JSON.stringify(value));
      } catch {
        // Ignore - best effort
      }
    }
  },
};

/**
 * Check if sessionStorage is available and working
 */
export function isStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (_e) {
    return false;
  }
}

export default safeSessionStorage;
